if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', class MediaGallery extends HTMLElement {
    constructor() {
      super();
      this.elements = {
        liveRegion: this.querySelector('[id^="GalleryStatus"]'),
        viewer: this.querySelector('[id^="GalleryViewer"]'),
        thumbnails: this.querySelector('[id^="GalleryThumbnails"]')
      };
      this.mql = window.matchMedia('(min-width: 750px)');
      this.selection = 0;
      this.loads = new WeakMap();
      if (!this.elements.viewer) return;
      this.elements.viewer.addEventListener('slideChanged', event => {
        const item = event.detail?.currentElement;
        if (item) this.setActiveMedia(item.dataset.mediaId, false, false);
      });
      this.elements.thumbnails?.querySelectorAll('[data-target]').forEach(item =>
        item.querySelector('button')?.addEventListener('click', () => this.setActiveMedia(item.dataset.target, false))
      );
      if ((this.dataset.desktopLayout || '').includes('thumbnail') && this.mql.matches) this.removeListSemantic();
      const first = this.elements.viewer.querySelector('.is-active') || this.items()[0];
      this.pendingMedia = first;
      const initial = this.selection;
      if (first) this.prepare(first).then(() => this.preloadNext(first)).catch(() => { if (initial === this.selection && this.isConnected) this.notice(first, true); });
    }

    items() { return [...this.elements.viewer.querySelectorAll('.product__media-item[data-media-id]')]; }

    prepare(item, retry = false) {
      if (item.querySelector('deferred-media, product-model, .deferred-media')) return Promise.resolve();
      const image = item.querySelector('.product__media img');
      if (!image) return Promise.resolve();
      image.fetchPriority = 'high';
      image.decoding = 'async';
      if (!retry && image.complete && image.naturalWidth) return Promise.resolve();
      if (!retry && this.loads.has(image)) return this.loads.get(image);
      const promise = new Promise((resolve, reject) => {
        let timer, settled = false;
        const finish = error => {
          if (settled) return;
          settled = true;
          clearTimeout(timer);
          image.removeEventListener('load', loaded);
          image.removeEventListener('error', failed);
          if (error) reject(error); else resolve();
        };
        const loaded = () => {
          if (!image.naturalWidth) { failed(); return; }
          finish();
        };
        const failed = () => finish(new Error('image-unavailable'));
        image.addEventListener('load', loaded, {once:true});
        image.addEventListener('error', failed, {once:true});
        timer = setTimeout(() => {
          image.addEventListener('load', () => {
            if (this.isConnected && this.pendingMedia === item && image.naturalWidth) this.setActiveMedia(item.dataset.mediaId, false, false);
          }, {once:true});
          failed();
        }, 12000);
        image.loading = 'eager';
        if (retry) {
          const url = image.currentSrc || image.src;
          image.removeAttribute('srcset');
          image.src = url;
        }
        if (image.complete) image.naturalWidth ? loaded() : failed();
      });
      this.loads.set(image, promise);
      promise.catch(() => { if (this.loads.get(image) === promise) this.loads.delete(image); });
      return promise;
    }

    preloadNext(item) {
      if (this.preloadIdle != null) window.cancelIdleCallback?.(this.preloadIdle);
      if (this.preloadTimer != null) clearTimeout(this.preloadTimer);
      const ticket = this.preloadGeneration = (this.preloadGeneration || 0) + 1;
      if (!this.isConnected || document.hidden || navigator.connection?.saveData) return;
      const items = this.items();
      const index = items.indexOf(item);
      const neighbors = [items[index + 1], items[index - 1]].filter(next => next && !next.querySelector('deferred-media, product-model, .deferred-media'));
      if (!neighbors.length) return;
      const warm = () => {
        if (ticket !== this.preloadGeneration || !this.isConnected || document.hidden || navigator.connection?.saveData) return;
        if (document.querySelector('product-form [aria-busy="true"]') || window.yeverCheckoutBusy) return;
        neighbors.forEach(next => {
          const image = next.querySelector('.product__media img');
          if (!image || image.complete && image.naturalWidth) return;
          image.fetchPriority = 'low';
          image.decoding = 'async';
          image.loading = 'eager';
        });
      };
      if (typeof window.requestIdleCallback === 'function') this.preloadIdle = window.requestIdleCallback(warm, {timeout:300});
      else this.preloadTimer = setTimeout(warm, 150);
    }

    notice(item, failed) {
      if (!this.status) {
        this.status = document.createElement('div');
        this.status.className = 'doma-gallery-status';
        this.status.setAttribute('role', 'status');
      }
      const current = this.elements.viewer.querySelector('.product__media-item.is-active');
      const thumbnailLayout = this.mql.matches && (this.dataset.desktopLayout || '').includes('thumbnail');
      const shown = thumbnailLayout ? current || item : item;
      const frame = shown.querySelector('.product-media-container') || shown;
      this.placeholder?.remove();
      this.placeholder = null;
      const previous = current?.querySelector('.product__media img');
      if (!failed && !this.mql.matches && current !== item && previous?.complete && previous.naturalWidth) {
        this.placeholder = document.createElement('img');
        this.placeholder.className = 'doma-gallery-placeholder';
        this.placeholder.alt = '';
        this.placeholder.setAttribute('aria-hidden', 'true');
        this.placeholder.src = previous.currentSrc || previous.src;
        frame.append(this.placeholder);
      }
      frame.append(this.status);
      this.status.classList.toggle('is-error', failed);
      this.status.replaceChildren(document.createTextNode(failed ? 'Foto indisponível' : 'Carregando…'));
      if (failed) {
        const button = document.createElement('button');
        button.type = 'button';
        button.textContent = '↻ Recarregar';
        button.setAttribute('aria-label', 'Recarregar foto');
        button.addEventListener('click', () => this.setActiveMedia(item.dataset.mediaId, false, true, true));
        this.status.append(button);
      }
      this.status.hidden = false;
    }

    async setActiveMedia(mediaId, prepend, scroll = true, retry = false) {
      const active = this.items().find(item => item.dataset.mediaId === mediaId) || this.items()[0];
      if (!active) return;
      this.preloadGeneration = (this.preloadGeneration || 0) + 1;
      const ticket = ++this.selection;
      this.pendingMedia = active;
      const image = active.querySelector('.product__media img');
      if (retry || image && (!image.complete || !image.naturalWidth)) this.notice(active, false);
      try {
        await this.prepare(active, retry);
      } catch {
        if (ticket === this.selection && this.isConnected) this.notice(active, true);
        return;
      }
      if (ticket !== this.selection || !this.isConnected) return;
      if (this.status) this.status.hidden = true;
      this.placeholder?.remove();
      this.placeholder = null;
      this.items().forEach(item => item.classList.toggle('is-active', item === active));
      const thumb = this.elements.thumbnails?.querySelector('[data-target="' + active.dataset.mediaId + '"]');
      if (prepend) {
        active.parentElement.prepend(active);
        if (thumb) thumb.parentElement.prepend(thumb);
        this.elements.viewer.resetPages?.();
      }
      this.preventStickyHeader();
      if (scroll && (!this.mql.matches || this.elements.thumbnails)) {
        active.parentElement.scrollTo({left:active.offsetLeft,behavior:'instant'});
      }
      if (scroll && active.getBoundingClientRect().top < -0.5) active.scrollIntoView({block:'start',behavior:'smooth'});
      this.playActiveMedia(active);
      this.setActiveThumbnail(thumb);
      this.announceLiveRegion(active, thumb?.dataset.mediaPosition || this.items().indexOf(active) + 1);
      this.preloadNext(active);
    }

    setActiveThumbnail(thumbnail) {
      if (!thumbnail || !this.elements.thumbnails) return;
      this.elements.thumbnails.querySelectorAll('button').forEach(button => button.removeAttribute('aria-current'));
      thumbnail.querySelector('button')?.setAttribute('aria-current', 'true');
      if (this.elements.thumbnails.isSlideVisible?.(thumbnail, 10)) return;
      this.elements.thumbnails.slider?.scrollTo({left:thumbnail.offsetLeft});
    }

    announceLiveRegion(active, position) {
      if (!this.elements.liveRegion || !active.querySelector('.product__media img')) return;
      this.elements.liveRegion.setAttribute('aria-hidden', 'false');
      this.elements.liveRegion.textContent = (window.accessibilityStrings?.imageAvailable || 'Foto [index] disponível').replace('[index]', position);
      clearTimeout(this.announcementTimer);
      this.announcementTimer = setTimeout(() => this.elements.liveRegion?.setAttribute('aria-hidden', 'true'), 2000);
    }

    playActiveMedia(active) {
      if (this.playingMedia === active) return;
      this.playingMedia = active;
      window.pauseAllMedia?.();
      active.querySelector('.deferred-media')?.loadContent(false);
    }

    preventStickyHeader() {
      this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
      this.stickyHeader?.dispatchEvent(new Event('preventHeaderReveal'));
    }

    removeListSemantic() {
      this.elements.viewer.slider?.setAttribute('role', 'presentation');
      this.elements.viewer.sliderItems?.forEach(slide => slide.setAttribute('role', 'presentation'));
    }
  });
}



